/**
 * Global SVG Cache Manager
 * Ensures SVGs are loaded only once and reused across all components
 */

class SvgCacheManager {
  constructor() {
    this.cache = new Map()
    this.loadingPromises = new Map()
  }

  /**
   * Get SVG content with optional color transformations
   * @param {string} src - SVG URL/path
   * @param {Object|null} colorMap - Color transformation map
   * @param {boolean} preserveColors - Whether to preserve original colors
   * @param {Object} vuetify - Vuetify instance for theme colors
   * @returns {Promise<string>} SVG content
   */
  async get(src, colorMap = null, preserveColors = false, vuetify = null) {
    const cacheKey = this._generateCacheKey(src, colorMap, preserveColors)

    // Return cached content if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)
    }

    // If already loading, return the existing promise
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey)
    }

    // Start loading
    const loadingPromise = this._loadSvg(src, colorMap, preserveColors, vuetify)
      .then(content => {
        this.cache.set(cacheKey, content)
        this.loadingPromises.delete(cacheKey)
        return content
      })
      .catch(error => {
        this.loadingPromises.delete(cacheKey)
        throw error
      })

    this.loadingPromises.set(cacheKey, loadingPromise)
    return loadingPromise
  }

  /**
   * Load SVG from URL and apply transformations
   * @private
   */
  async _loadSvg(src, colorMap, preserveColors, vuetify) {
    try {
      const response = await fetch(src)

      if (!response.ok) {
        throw new Error(`Failed to load SVG: ${response.status} ${response.statusText}`)
      }

      let svg = await response.text()

      if (!preserveColors) {
        svg = this._applyColorTransformations(svg, colorMap, vuetify)
      }

      return svg
    } catch (error) {
      console.error('Error loading SVG:', error, 'src:', src)
      throw error
    }
  }

  /**
   * Apply color transformations to SVG content
   * @private
   */
  _applyColorTransformations(svg, colorMap, vuetify) {
    if (colorMap && typeof colorMap === 'object') {
      Object.entries(colorMap).forEach(([originalColor, themeColor]) => {
        // Get actual color from Vuetify theme or use themeColor directly
        const actualColor = (vuetify && vuetify.theme && vuetify.theme.themes &&
                           vuetify.theme.themes.light && vuetify.theme.themes.light[themeColor]) || themeColor
        const normalizedOriginal = originalColor.toUpperCase().replace('#', '')
        const fillRegex = new RegExp(`fill="#?${normalizedOriginal}"`, 'gi')
        const strokeRegex = new RegExp(`stroke="#?${normalizedOriginal}"`, 'gi')

        svg = svg.replace(fillRegex, `fill="${actualColor}"`)
        svg = svg.replace(strokeRegex, `stroke="${actualColor}"`)
      })
    } else {
      svg = svg.replace(/fill="(?!none)[^"]*"/g, 'fill="currentColor"')
      svg = svg.replace(/stroke="(?!none)[^"]*"/g, 'stroke="currentColor"')
    }

    return svg
  }

  /**
   * Generate unique cache key
   * @private
   */
  _generateCacheKey(src, colorMap, preserveColors) {
    return colorMap
      ? `${src}_${JSON.stringify(colorMap)}_${preserveColors}`
      : `${src}_${preserveColors}`
  }

  /**
   * Clear cache (useful for testing or memory management)
   */
  clear() {
    this.cache.clear()
    this.loadingPromises.clear()
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      cachedItems: this.cache.size,
      pendingLoads: this.loadingPromises.size
    }
  }
}

// Export singleton instance
export default new SvgCacheManager()
